import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { classifySessionError, terminationReasonForCode } from '../session/session.models';
import { AUTH_RETRY_ATTEMPTED } from './http-context';

const REFRESHABLE_CODES = new Set([
  'ACCESS_TOKEN_EXPIRED',
  'ACCESS_TOKEN_INVALID',
  'TOKEN_EXPIRED',
  'SESSION_TOKEN_EXPIRED',
]);

const DIRECT_TERMINATION_CODES = new Set([
  'SESSION_REVOKED',
  'SESSION_EXPIRED',
  'ACCOUNT_INACTIVE',
]);

function isPublicAuthEndpoint(url: string): boolean {
  return [
    '/auth/login',
    '/auth/refresh',
    '/auth/csrf',
    '/auth/password-reset/request',
    '/auth/password-reset/validate',
    '/auth/password-reset/confirm',
  ].some((path) => url.split(/[?#]/, 1)[0].endsWith(path));
}

function requestStillMatchesSession(
  auth: AuthService,
  requestToken: string | null,
  requestSessionId: string | undefined,
): boolean {
  const latestToken = auth.getAccessToken();
  if (!latestToken) return false;
  const latestSessionId = auth.getSessionId();
  if (requestSessionId || latestSessionId) {
    return !!requestSessionId && requestSessionId === latestSessionId;
  }
  // During the one-release sid-less compatibility path, the bearer itself is
  // the only safe request/session identity available.
  return !!requestToken && requestToken === latestToken;
}

export const jwtInterceptor: HttpInterceptorFn = (request, next) => {
  const auth = inject(AuthService);
  const publicAuthRequest = isPublicAuthEndpoint(request.url);
  const token = auth.getAccessToken();
  const requestSessionId = auth.getSessionId();

  if (token && !publicAuthRequest) {
    request = request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      if (publicAuthRequest || request.context.get(AUTH_RETRY_ATTEMPTED)) {
        return throwError(() => error);
      }

      const classification = classifySessionError(error);
      const code = classification.code;

      if (code === 'INSTALLATION_ID_MISMATCH') {
        if (requestStillMatchesSession(auth, token, requestSessionId)) {
          auth.forceLogout('installation-mismatch');
        }
        return throwError(() => error);
      }

      if (code && DIRECT_TERMINATION_CODES.has(code)) {
        if (requestStillMatchesSession(auth, token, requestSessionId)) {
          auth.forceLogout(terminationReasonForCode(code));
        }
        return throwError(() => error);
      }

      // A present but unknown code is deliberately non-destructive. This lets
      // the backend add 401/403 business errors without silently logging out a
      // newer client.
      if (classification.codePresent && (!code || !REFRESHABLE_CODES.has(code))) {
        return throwError(() => error);
      }

      const shouldRefresh =
        (classification.codePresent && !!code && REFRESHABLE_CODES.has(code)) ||
        (!classification.codePresent && error.status === 401);

      if (shouldRefresh) {
        if (!auth.isAuthenticated()) return throwError(() => error);
        const latestToken = auth.getAccessToken();
        // A request can fail after another request has already completed the
        // shared rotation. Retry with that newer access token instead of
        // rotating the newly issued refresh token a second time. A different
        // sid means a new login won the race, so the old operation must never
        // be replayed under it.
        if (token && latestToken && latestToken !== token) {
          const latestSessionId = auth.getSessionId();
          const sameSession =
            requestSessionId && latestSessionId
              ? requestSessionId === latestSessionId
              : !requestSessionId && !latestSessionId;
          if (!sameSession) return throwError(() => error);
          return next(
            request.clone({
              setHeaders: { Authorization: `Bearer ${latestToken}` },
              context: request.context.set(AUTH_RETRY_ATTEMPTED, true),
            }),
          );
        }
        return auth.refreshToken().pipe(
          switchMap((response) =>
            next(
              request.clone({
                setHeaders: { Authorization: `Bearer ${response.accessToken}` },
                context: request.context.set(AUTH_RETRY_ATTEMPTED, true),
              }),
            ),
          ),
        );
      }

      // Uncoded 403 is a permission/billing rejection, not session death.
      return throwError(() => error);
    }),
  );
};
